export const FILTERED_VALUE = '[Filtered]';

const
    CIRCULAR_VALUE = '[Circular]',
    MAX_DEPTH = 8,
    MAX_ARRAY_LENGTH = 50,
    MAX_OBJECT_KEYS = 100,
    SECRET_KEY = /password|passphrase|passwd|token|secret|apikey|privatekey|authorization|cookie|sessionid|securitycode|cvv|cvc|pendingorder/i,
    SENSITIVE_KEY = /^(?:accountNumber|address|address1|address2|billingAddress|billingAddressId|cardholder|cardNumber|county|creditCard|dentalLicense|dentistFullName|email|emailAddress|firstName|fullName|ipAddress|lastName|officeAddress|officePhone|payment|paymentInput|phone|practiceName|shippingAddress|socialSecurityNumber|ssn|telephone|username|body|requestBody|responseBody|postData|formData|variables|payload|input|config)$/i,
    URL_KEY = /^(?:documentURL|referer|referrer|requestURL|url|href|from|to|filename|abspath)$/i,
    SAFE_FIELDS = {
        headers: /^(?:accept|contenttype|useragent|referer|referrer)$/i,
        user: /^id$/i,
        request: /^(?:url|method|headers)$/i,
        response: /^(?:status|statuscode|headers)$/i
    },
    URL_VALUE = /(?:[a-z][a-z0-9+.-]*:\/\/|\/)[^\s<>"')]+/gi,
    EMAIL_VALUE = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi,
    BEARER_VALUE = /\bBearer\s+[A-Z0-9._~+/=-]+/gi,
    NAMED_VALUE = /["']?[A-Z][A-Z0-9_-]*["']?\s*[:=]/gi;

function normalizeKey ( key ) {
    return key.replace( /[^a-z0-9]/gi, '' ).toLowerCase();
}

function isSensitiveKey ( key ) {
    return SECRET_KEY.test( key ) || SENSITIVE_KEY.test( key );
}

function sanitizeString ( value ) {
    const text = value.replace( URL_VALUE, sanitizeUrl );
    // Discard secret-bearing text instead of guessing where a quoted/serialized value ends.
    const fields = text.replace( /\\+(?=["'])/g, '' ).match( NAMED_VALUE ) || [];
    if ( fields.some( field => isSensitiveKey( normalizeKey( field ) ) ) ) {
        return FILTERED_VALUE;
    }
    return text
        .replace( EMAIL_VALUE, '[Filtered Email]' )
        .replace( BEARER_VALUE, 'Bearer [Filtered]' );
}

function sanitizeUrl ( value ) {
    if ( typeof value !== 'string' ) {
        return FILTERED_VALUE;
    }

    const queryIndex = value.indexOf( '?' );
    const hashIndex = value.indexOf( '#' );
    const firstPrivateIndex = [queryIndex, hashIndex]
        .filter( index => index >= 0 )
        .reduce( ( first, index ) => Math.min( first, index ), value.length );

    const marker = firstPrivateIndex === value.length ? '' :
        queryIndex === firstPrivateIndex ? '?[Filtered]' : '#[Filtered]';
    return value.slice( 0, firstPrivateIndex )
        .replace( /\/\/[^/@\s]+@/g, '//[Filtered]@' )
        .replace( EMAIL_VALUE, '[Filtered Email]' ) + marker;
}

function sanitizeError ( error, seen, depth ) {
    const sanitized = {
        name: sanitizeString( error.name || 'Error' ),
        message: sanitizeString( error.message || '' )
    };

    if ( error.stack ) {
        sanitized.stack = sanitizeString( error.stack );
    }

    Object.keys( error ).slice( 0, MAX_OBJECT_KEYS ).forEach( key => {
        sanitized[key] = sanitizeEntry( key, error[key], seen, depth + 1 );
    } );

    return sanitized;
}

function sanitizeObject ( value, seen, depth, allowedKeys ) {
    if ( seen.indexOf( value ) >= 0 ) {
        return CIRCULAR_VALUE;
    }

    if ( depth > MAX_DEPTH ) {
        return '[Truncated]';
    }

    const nextSeen = seen.concat( [value] );

    if ( value instanceof Error ) {
        return sanitizeError( value, nextSeen, depth );
    }

    if ( value instanceof Date ) {
        return value.toISOString();
    }

    if ( Array.isArray( value ) ) {
        return value.slice( 0, MAX_ARRAY_LENGTH )
            .map( item => sanitizeValue( item, nextSeen, depth + 1 ) );
    }

    return Object.keys( value ).slice( 0, MAX_OBJECT_KEYS ).reduce( ( sanitized, key ) => {
        sanitized[key] = allowedKeys && !allowedKeys.test( normalizeKey( key ) ) ?
            FILTERED_VALUE : sanitizeEntry( key, value[key], nextSeen, depth + 1 );
        return sanitized;
    }, {} );
}

function sanitizeEntry ( key, value, seen, depth ) {
    key = normalizeKey( key );
    if ( isSensitiveKey( key ) || /^data$/i.test( key ) && typeof value === 'string' ) {
        return FILTERED_VALUE;
    }

    if ( Object.prototype.hasOwnProperty.call( SAFE_FIELDS, key ) ) {
        return value && typeof value === 'object' && !Array.isArray( value ) ?
            sanitizeObject( value, seen, depth, SAFE_FIELDS[key] ) : FILTERED_VALUE;
    }

    if ( URL_KEY.test( key ) ) {
        return sanitizeString( sanitizeUrl( value ) );
    }

    return sanitizeValue( value, seen, depth );
}

function sanitizeValue ( value, seen = [], depth = 0 ) {
    if ( typeof value === 'string' ) {
        return sanitizeString( value );
    }

    if ( value == null || typeof value === 'number' || typeof value === 'boolean' ) {
        return value;
    }

    if ( typeof value === 'object' ) {
        return sanitizeObject( value, seen, depth );
    }

    return String( value );
}

export function sanitizeForLogging ( value ) {
    return sanitizeValue( value );
}
